/**
 * Zustandspille (Musterkatalog M-001, Design-Guide `zustaende-risiko-warnung.md`): Risiko- und
 * Warnzustände auf Karten, Zeilen und Kacheln als Pille mit hellem Grund; Rot nie als Fließtext.
 * Töne sind feste, vollständige Klassenketten (nie zusammengesetzt).
 *
 * - `gefahr`: Priorität „Dringend“, „Blockiert“ (Rand, `red-100`/`red-800`).
 * - `mitPunkt` (nur Ton `gefahr`): Punkt-Marker vor dem Text; Feldfehler (M-002 F3, AW-005).
 * - `ueberfaellig`: Pille mit Punkt-Marker vor dem Text (`red-100`/`red-700`); das Datum steht
 *   beim Aufrufer daneben und bleibt grau.
 *
 * `ZustandspilleKnopf` ist die bedienbare Variante („Blockiert · n“ als Abhängigkeits-Fokus).
 */
import Link from "next/link";

export type ZustandspilleTon = "gefahr" | "ueberfaellig";

const PILLE_KLASSEN: Record<ZustandspilleTon, string> = {
  gefahr: "py-0.5 px-2 inline-flex items-center gap-x-1.5 text-xs font-medium rounded-full border border-red-200 bg-red-100 text-red-800",
  ueberfaellig: "py-0.5 px-2 inline-flex items-center gap-x-1.5 text-xs font-medium rounded-full bg-red-100 text-red-700",
};

const KNOPF_KLASSE =
  "relative z-10 py-0.5 px-2 inline-flex items-center gap-x-1 text-xs font-medium rounded-full border border-red-200 bg-red-100 text-red-800 hover:bg-red-200 focus:outline-hidden focus-visible:ring-2 focus-visible:ring-primary disabled:opacity-50 disabled:pointer-events-none";

export function Zustandspille({
  ton,
  children,
  label,
  mitPunkt = false,
}: {
  ton: ZustandspilleTon;
  children: React.ReactNode;
  /** Zugänglicher Name, wenn der sichtbare Text allein nicht trägt. */
  label?: string;
  /** Punkt-Marker vor dem Text (Ton `gefahr`; bei `ueberfaellig` immer gesetzt). */
  mitPunkt?: boolean;
}) {
  return (
    <>
      {/* Preline: productivity/productivity-sub-planning-boards/productivity-cat-kanban-board-cards/task-card-with-issue-tags-and-progress-bar (Pille „High“) und templates/productivity/project-management/index.html (Pille „At risk“ mit Punkt) */}
      <span className={PILLE_KLASSEN[ton]} aria-label={label}>
        {(ton === "ueberfaellig" || mitPunkt) && <span className="size-2 inline-block rounded-full bg-current" aria-hidden="true" />}
        {children}
      </span>
    </>
  );
}

// Feste, vollständige Klassenketten (nie zusammengesetzt); `relative z-10` legt die Pille über den Zeilenlink einer Liste.
const LINK_KLASSE: Record<ZustandspilleTon, string> = {
  gefahr:
    "relative z-10 py-0.5 px-2 inline-flex items-center gap-x-1.5 text-xs font-medium rounded-full border border-red-200 bg-red-100 text-red-800 hover:bg-red-200 focus:outline-hidden focus-visible:ring-2 focus-visible:ring-primary whitespace-nowrap",
  ueberfaellig:
    "relative z-10 py-0.5 px-2 inline-flex items-center gap-x-1.5 text-xs font-medium rounded-full bg-red-100 text-red-700 hover:bg-red-200 focus:outline-hidden focus-visible:ring-2 focus-visible:ring-primary whitespace-nowrap",
};

/**
 * Zustandspille als Link (M-015 AV1: „3 blockiert“ führt gefiltert ins Brett). Sie liegt über dem Zeilenlink einer
 * Liste (`relative z-10`), damit der Klick auf die Pille nicht die Zeile trifft. Immer mit Punkt-Marker; der Text trägt
 * die Aussage (Zahl und Wort), nie nur die Farbe.
 */
export function ZustandspilleLink({
  ton,
  href,
  children,
  label,
}: {
  ton: ZustandspilleTon;
  href: string;
  children: React.ReactNode;
  label?: string;
}) {
  return (
    <>
      {/* Preline: productivity/productivity-sub-planning-boards/productivity-cat-kanban-board-cards/task-card-with-issue-tags-and-progress-bar (Pille „High“) und templates/productivity/project-management/index.html (Pille „At risk“ mit Punkt) */}
      <Link className={LINK_KLASSE[ton]} href={href} aria-label={label}>
        <span className="size-2 inline-block rounded-full bg-current" aria-hidden="true" />
        {children}
      </Link>
    </>
  );
}

export function ZustandspilleKnopf({
  children,
  titel,
  gedrueckt,
  onClick,
  disabled,
}: {
  children: React.ReactNode;
  titel?: string;
  gedrueckt?: boolean;
  onClick?: () => void;
  disabled?: boolean;
}) {
  return (
    <>
      {/* Preline: productivity/productivity-sub-planning-boards/productivity-cat-kanban-board-cards/task-card-with-issue-tags-and-progress-bar (Pille „High“) */}
      <button type="button" className={KNOPF_KLASSE} title={titel} aria-pressed={gedrueckt} onClick={onClick} disabled={disabled}>
        {children}
      </button>
    </>
  );
}
